import { useState, useEffect } from "react";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  bg:        "#F0F4F8",
  bgMuted:   "#E8EDF3",
  surface:   "#FFFFFF",
  border:    "#E2E8F0",
  teal:      "#0D9488",
  tealDark:  "#0F766E",
  tealPale:  "#F0FDFA",
  tealBorder:"#99F6E4",
  navy:      "#0F172A",
  dark:      "#1E293B",
  mid:       "#475569",
  muted:     "#94A3B8",
  light:     "#CBD5E1",
  green:     "#059669",
  amber:     "#D97706",
  red:       "#DC2626",
};

const ASSESSMENT_URL = "/assessment";
const CALENDLY_URL   = "https://calendly.com/yourname/strategy-call"; // ← replace

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function useScrolled(threshold = 20) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > threshold);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, [threshold]);
  return scrolled;
}

// ─── HEADER ───────────────────────────────────────────────────────────────────
function Header() {
  const scrolled = useScrolled();
  const [menuOpen, setMenuOpen] = useState(false);

  const nav = [
    { label: "Assessment", href: "#assessment" },
    { label: "Process",    href: "#process"    },
  ];

  return (
    <header style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      transition: "all 0.3s ease",
      background: scrolled ? "rgba(240,244,248,0.95)" : "transparent",
      backdropFilter: scrolled ? "blur(12px)" : "none",
      borderBottom: scrolled ? `1px solid ${T.border}` : "1px solid transparent",
      boxShadow: scrolled ? "0 1px 8px rgba(0,0,0,0.06)" : "none",
    }}>
      <div style={{ maxWidth: 1152, margin: "0 auto", padding: "0 24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 68 }}>

          {/* Logo */}
          <a href="/" style={{ display: "flex", alignItems: "center", gap: 9, textDecoration: "none" }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: T.teal, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ color: "#fff", fontSize: 16, lineHeight: 1 }}>✦</span>
            </div>
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 16, fontWeight: 700, color: T.navy, letterSpacing: "-0.01em" }}>
              Service<span style={{ color: T.teal }}>Solutions</span>Pro
            </span>
          </a>

          {/* Desktop nav */}
          <nav style={{ display: "flex", alignItems: "center", gap: 32 }} className="desktop-nav">
            {nav.map(item => (
              <a key={item.label} href={item.href} style={{ fontSize: 14, fontWeight: 500, color: T.mid, textDecoration: "none", transition: "color 0.15s" }}
                onMouseEnter={e => e.target.style.color = T.navy}
                onMouseLeave={e => e.target.style.color = T.mid}>
                {item.label}
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          <a href={ASSESSMENT_URL} style={{ display: "block", textDecoration: "none" }} className="desktop-cta">
            <button style={{
              background: T.teal, color: "#fff", border: "none", borderRadius: 9,
              padding: "9px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer",
              fontFamily: "'DM Sans',sans-serif", transition: "all 0.15s",
              boxShadow: "0 2px 10px rgba(13,148,136,0.3)",
            }}
              onMouseEnter={e => { e.target.style.background = T.tealDark; e.target.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.target.style.background = T.teal; e.target.style.transform = "none"; }}>
              Get Started
            </button>
          </a>

          {/* Mobile hamburger */}
          <button onClick={() => setMenuOpen(!menuOpen)}
            style={{ display: "none", background: "none", border: "none", cursor: "pointer", padding: 6, color: T.dark, fontSize: 20 }}
            className="mobile-menu-btn">
            {menuOpen ? "✕" : "☰"}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div style={{ borderTop: `1px solid ${T.border}`, padding: "12px 0 16px" }}>
            {nav.map(item => (
              <a key={item.label} href={item.href} onClick={() => setMenuOpen(false)}
                style={{ display: "block", padding: "10px 16px", fontSize: 14, fontWeight: 500, color: T.mid, textDecoration: "none", borderRadius: 8, transition: "all 0.15s" }}
                onMouseEnter={e => { e.target.style.background = T.bgMuted; e.target.style.color = T.navy; }}
                onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.color = T.mid; }}>
                {item.label}
              </a>
            ))}
            <div style={{ padding: "8px 16px 0" }}>
              <a href={ASSESSMENT_URL} style={{ display: "block", textAlign: "center", background: T.teal, color: "#fff", borderRadius: 9, padding: "11px", fontSize: 14, fontWeight: 700, textDecoration: "none" }}>
                Get Started
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

// ─── HERO ─────────────────────────────────────────────────────────────────────
function Hero() {
  const stats = [
    { icon: "⏱", value: "<30s",  label: "Average Response Time" },
    { icon: "📈", value: "47%",   label: "Increase in Consultations" },
    { icon: "🛡", value: "24/7",  label: "Autonomous Coverage"  },
  ];

  return (
    <section style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: `linear-gradient(180deg, ${T.bg} 0%, ${T.bgMuted} 100%)`,
      paddingTop: 68, position: "relative", overflow: "hidden",
    }}>
      {/* Subtle grid */}
      <div style={{
        position: "absolute", inset: 0, opacity: 0.025,
        backgroundImage: `linear-gradient(to right, #64748B 1px, transparent 1px), linear-gradient(to bottom, #64748B 1px, transparent 1px)`,
        backgroundSize: "60px 60px",
      }} />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 900, margin: "0 auto", padding: "80px 24px", textAlign: "center" }}>

        {/* Trust badge */}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 20, padding: "6px 16px", marginBottom: 32, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
          <span style={{ color: T.teal, fontSize: 14 }}>🛡</span>
          <span style={{ fontSize: 13, color: T.mid, fontWeight: 500 }}>Built exclusively for solo plastic surgery practices</span>
        </div>

        {/* Headline */}
        <h1 style={{ fontSize: "clamp(36px,6vw,64px)", fontWeight: 700, color: T.navy, lineHeight: 1.1, letterSpacing: "-0.03em", marginBottom: 20, fontFamily: "'DM Sans',sans-serif" }}>
          Find out exactly how much revenue<br />
          <span style={{ color: T.teal }}>your practice is losing</span> every month
        </h1>

        {/* Subheadline */}
        <p style={{ fontSize: 18, color: T.mid, maxWidth: 620, margin: "0 auto 36px", lineHeight: 1.65 }}>
          Stop losing high-value surgical leads to slow response times and manual processes.
          Our 2-minute AI assessment shows you exactly where the leaks are.
        </p>

        {/* CTAs */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, flexWrap: "wrap", marginBottom: 64 }}>
          <a href={ASSESSMENT_URL} style={{ textDecoration: "none" }}>
            <button style={{
              background: `linear-gradient(135deg,${T.teal},#0891B2)`, color: "#fff",
              border: "none", borderRadius: 10, padding: "15px 32px",
              fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
              boxShadow: "0 6px 24px rgba(13,148,136,0.35)", transition: "all 0.15s",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
              onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
              onMouseLeave={e => e.currentTarget.style.transform = "none"}>
              Start Your AI Assessment →
            </button>
          </a>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 0, maxWidth: 640, margin: "0 auto", borderTop: `1px solid ${T.border}`, paddingTop: 32 }}>
          {stats.map((s, i) => (
            <div key={i} style={{ textAlign: "center", padding: "0 16px", borderRight: i < 2 ? `1px solid ${T.border}` : "none" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: T.teal, marginBottom: 6 }}>
                <span style={{ fontSize: 18 }}>{s.icon}</span>
                <span style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.03em", fontFamily: "'DM Sans',sans-serif" }}>{s.value}</span>
              </div>
              <div style={{ fontSize: 13, color: T.muted }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── PROBLEM SECTION ──────────────────────────────────────────────────────────
function ProblemSection() {
  const problems = [
    { icon: "📵", title: "Leads Going Cold", desc: "A potential patient calls after hours or fills out your web form. By the time someone responds, they've already booked with the practice that called back in 5 minutes." },
    { icon: "🚶", title: "Consults That Don't Convert", desc: "40–70% of patients who consult never book on the day. Without a structured follow-up, most of them never hear from you again — even though they're still deciding." },
    { icon: "⏰", title: "Hours Lost to Manual Work", desc: "Confirmation calls, pre-op instructions, review requests, appointment reminders — all done manually, all eating into time that should be spent on patients." },
  ];

  return (
    <section style={{ padding: "96px 24px", background: T.bg }}>
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: T.tealPale, border: `1px solid ${T.tealBorder}`, borderRadius: 20, padding: "5px 14px", marginBottom: 16 }}>
            <span style={{ color: T.teal, fontSize: 12 }}>●</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.tealDark }}>The Problem</span>
          </div>
          <h2 style={{ fontSize: "clamp(26px,4vw,40px)", fontWeight: 700, color: T.navy, letterSpacing: "-0.025em", marginBottom: 14, fontFamily: "'DM Sans',sans-serif" }}>
            Where solo practices lose the most revenue
          </h2>
          <p style={{ fontSize: 16, color: T.mid, maxWidth: 560, margin: "0 auto", lineHeight: 1.65 }}>
            These three gaps exist in almost every solo plastic surgery practice. They're invisible until you measure them.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 20 }}>
          {problems.map((p, i) => (
            <div key={i} style={{
              background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
              padding: "28px 26px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
              transition: "all 0.2s", borderTop: `3px solid ${T.teal}`,
            }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 8px 28px rgba(0,0,0,0.08)"}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.05)"}>
              <div style={{ fontSize: 28, marginBottom: 14 }}>{p.icon}</div>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: T.navy, marginBottom: 10, fontFamily: "'DM Sans',sans-serif" }}>{p.title}</h3>
              <p style={{ fontSize: 14, color: T.mid, lineHeight: 1.65, margin: 0 }}>{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── ASSESSMENT SECTION ───────────────────────────────────────────────────────
function AssessmentSection() {
  return (
    <section id="assessment" style={{ padding: "96px 24px", background: T.bgMuted }}>
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: T.tealPale, border: `1px solid ${T.tealBorder}`, borderRadius: 20, padding: "5px 14px", marginBottom: 16 }}>
            <span style={{ fontSize: 14 }}>🧮</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.tealDark }}>Practice Analysis</span>
          </div>
          <h2 style={{ fontSize: "clamp(26px,4vw,40px)", fontWeight: 700, color: T.navy, letterSpacing: "-0.025em", marginBottom: 14, fontFamily: "'DM Sans',sans-serif" }}>
            The Practice Efficiency Audit
          </h2>
          <p style={{ fontSize: 16, color: T.mid, maxWidth: 520, margin: "0 auto", lineHeight: 1.65 }}>
            13 questions. 2 minutes. A personalised revenue leakage report with specific AI automations to close each gap.
          </p>
        </div>

        {/* Preview card */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.07)" }}>

          {/* Card header */}
          <div style={{ borderBottom: `1px solid ${T.border}`, padding: "22px 28px" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 4 }}>Your Practice Metrics</div>
            <div style={{ fontSize: 13, color: T.muted }}>Answer 13 questions to receive your personalised revenue and time analysis</div>
          </div>

          {/* Preview fields — decorative */}
          <div style={{ padding: "28px 28px 8px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 20 }}>
              {[
                { label: "Monthly Consultations",    hint: "Avg # per month"       },
                { label: "Avg Procedure Value ($)",  hint: "Typical surgical revenue" },
                { label: "Lead Response Time",       hint: "Hours to first contact"  },
                { label: "Google Review Count",      hint: "Current # of reviews"    },
              ].map((f, i) => (
                <div key={i}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.dark, marginBottom: 6 }}>{f.label}</div>
                  <div style={{ background: T.bg, border: `1.5px solid ${T.border}`, borderRadius: 8, padding: "11px 13px", fontSize: 13, color: T.muted }}>{f.hint}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 12, color: T.muted, marginBottom: 24, fontStyle: "italic" }}>+ 9 more questions covering consultation pipeline, manual tasks, and reactivation</div>
          </div>

          {/* CTA inside card */}
          <div style={{ padding: "0 28px 28px" }}>
            <a href={ASSESSMENT_URL} style={{ display: "block", textDecoration: "none" }}>
              <button style={{
                width: "100%", background: `linear-gradient(135deg,${T.teal},#0891B2)`,
                color: "#fff", border: "none", borderRadius: 10, padding: "14px",
                fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
                boxShadow: "0 4px 18px rgba(13,148,136,0.3)", transition: "all 0.15s",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
                onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
                onMouseLeave={e => e.currentTarget.style.transform = "none"}>
                Start Your AI Assessment →
              </button>
            </a>
            <div style={{ textAlign: "center", fontSize: 12, color: T.muted, marginTop: 10 }}>
              No email required to start · Results in 2 minutes
            </div>
          </div>

          {/* What you get */}
          <div style={{ borderTop: `1px solid ${T.border}`, padding: "20px 28px", background: T.bg, display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
            {[
              { icon: "💰", label: "Revenue Leak Score",    sub: "Dollar figure per month" },
              { icon: "⏱",  label: "Time Suck Score",       sub: "Hours lost to manual work" },
              { icon: "🤖", label: "AI Action Plan",        sub: "Personalised recommendations" },
            ].map((item, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 20, marginBottom: 5 }}>{item.icon}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.dark, marginBottom: 3 }}>{item.label}</div>
                <div style={{ fontSize: 11, color: T.muted }}>{item.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}



// ─── PROCESS SECTION ──────────────────────────────────────────────────────────
function ProcessSection() {
  const steps = [
    { num: "01", icon: "🧮", title: "Take the AI Assessment", desc: "Answer 13 questions about your practice. See your revenue leak score, time suck score, and a personalised automation roadmap — immediately." },
    { num: "02", icon: "📋", title: "Review Your Strategy Call", desc: "We walk through your full personalised report together, confirm which automations close the most leakage first, and map your 30-day plan." },
    { num: "03", icon: "⚡", title: "Go Live in 7 Days", desc: "We build everything done-for-you. No tech skills required from you or your staff. Every automation is configured, tested, and running within a week." },
  ];

  return (
    <section id="process" style={{ padding: "96px 24px", background: T.bgMuted }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <h2 style={{ fontSize: "clamp(26px,4vw,40px)", fontWeight: 700, color: T.navy, letterSpacing: "-0.025em", marginBottom: 14, fontFamily: "'DM Sans',sans-serif" }}>
            The Practice Growth Roadmap
          </h2>
          <p style={{ fontSize: 16, color: T.mid, maxWidth: 480, margin: "0 auto", lineHeight: 1.65 }}>
            A three-step process to close your revenue leaks without disrupting how you practice.
          </p>
        </div>

        {/* Timeline */}
        <div style={{ position: "relative" }}>
          {/* Center line */}
          <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 1, background: T.border, transform: "translateX(-50%)" }} />

          {steps.map((step, i) => {
            const isLeft = i % 2 === 0;
            return (
              <div key={i} style={{ position: "relative", marginBottom: i < steps.length - 1 ? 48 : 0, display: "flex", justifyContent: isLeft ? "flex-start" : "flex-end" }}>
                {/* Node */}
                <div style={{ position: "absolute", left: "50%", top: 28, width: 16, height: 16, borderRadius: "50%", background: T.teal, border: `4px solid ${T.bgMuted}`, transform: "translateX(-50%)", zIndex: 1 }} />
                {/* Card */}
                <div style={{
                  width: "calc(50% - 36px)", background: T.surface, border: `1px solid ${T.border}`,
                  borderRadius: 12, padding: "22px 22px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                  transition: "all 0.2s",
                }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = "0 8px 28px rgba(0,0,0,0.09)"}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.05)"}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: T.teal, background: T.tealPale, border: `1px solid ${T.tealBorder}`, padding: "2px 8px", borderRadius: 4 }}>STEP {step.num}</span>
                    <div style={{ width: 32, height: 32, background: T.bg, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>{step.icon}</div>
                  </div>
                  <h3 style={{ fontSize: 17, fontWeight: 700, color: T.navy, marginBottom: 8, fontFamily: "'DM Sans',sans-serif" }}>{step.title}</h3>
                  <p style={{ fontSize: 13, color: T.mid, lineHeight: 1.65, margin: 0 }}>{step.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── CTA SECTION ──────────────────────────────────────────────────────────────
function CTASection() {
  return (
    <section id="cta" style={{ padding: "96px 24px", background: T.bg }}>
      <div style={{ maxWidth: 700, margin: "0 auto", textAlign: "center" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: T.tealPale, border: `1px solid ${T.tealBorder}`, borderRadius: 20, padding: "5px 14px", marginBottom: 20 }}>
          <span style={{ fontSize: 14 }}>📅</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: T.tealDark }}>Limited Availability</span>
        </div>
        <h2 style={{ fontSize: "clamp(28px,5vw,48px)", fontWeight: 700, color: T.navy, letterSpacing: "-0.03em", marginBottom: 16, lineHeight: 1.15, fontFamily: "'DM Sans',sans-serif" }}>
          Stop the leak today
        </h2>
        <p style={{ fontSize: 17, color: T.mid, maxWidth: 480, margin: "0 auto 36px", lineHeight: 1.65 }}>
          Every day without AI-powered patient acquisition is revenue walking out the door. Start with the assessment — it takes 2 minutes.
        </p>
        <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginBottom: 28 }}>
          <a href={ASSESSMENT_URL} style={{ textDecoration: "none" }}>
            <button style={{
              background: `linear-gradient(135deg,${T.teal},#0891B2)`, color: "#fff",
              border: "none", borderRadius: 10, padding: "16px 36px",
              fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
              boxShadow: "0 6px 24px rgba(13,148,136,0.35)", transition: "all 0.15s",
            }}
              onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
              onMouseLeave={e => e.currentTarget.style.transform = "none"}>
              Take the 2-Minute Assessment →
            </button>
          </a>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
            <button style={{
              background: T.surface, color: T.dark, border: `1.5px solid ${T.border}`,
              borderRadius: 10, padding: "15px 28px", fontSize: 16, fontWeight: 600,
              cursor: "pointer", fontFamily: "'DM Sans',sans-serif", transition: "all 0.15s",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = T.teal; e.currentTarget.style.color = T.teal; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.dark; }}>
              Book a Strategy Call
            </button>
          </a>
        </div>
        {/* Trust indicators */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, flexWrap: "wrap", fontSize: 13, color: T.muted }}>
          {["No email required to start", "No long-term contracts", "Results in 2 minutes"].map((t, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: T.teal, fontSize: 12 }}>✓</span>
              {t}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FOOTER ───────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer style={{ padding: "36px 24px", background: T.surface, borderTop: `1px solid ${T.border}` }}>
      <div style={{ maxWidth: 1152, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: T.teal, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#fff", fontSize: 12 }}>✦</span>
          </div>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.navy, fontFamily: "'DM Sans',sans-serif" }}>
            Service<span style={{ color: T.teal }}>Solutions</span>Pro
          </span>
        </div>
        <div style={{ display: "flex", gap: 24, fontSize: 13, color: T.muted }}>
          {["Privacy", "Terms"].map(l => (
            <a key={l} href="#" style={{ color: T.muted, textDecoration: "none", transition: "color 0.15s" }}
              onMouseEnter={e => e.target.style.color = T.dark}
              onMouseLeave={e => e.target.style.color = T.muted}>{l}</a>
          ))}
        </div>
        <div style={{ fontSize: 13, color: T.muted }}>
          © {new Date().getFullYear()} Service Solutions Pro. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", color: T.dark, background: T.bg }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;1,9..40,400&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .desktop-cta { display: none !important; }
          .mobile-menu-btn { display: block !important; }
        }
        @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        main > * { animation: fadeUp 0.5s ease forwards; }
      `}</style>
      <Header />
      <main>
        <Hero />
        <ProblemSection />
        <AssessmentSection />
        <ProcessSection />
        <CTASection />
        <Footer />
      </main>
    </div>
  );
}
